/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { ActionRequest, ActionsEventActionNames, Ducks, User } from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, call, select, takeEvery } from 'redux-saga/effects';
import { StoreState } from '../../store';
import { addSettingsActionEvent } from './actionsEvent';

export enum ActionType {
  SET_USERS = 'app/actionsWatcher/SetUsers',
}

interface Meta {
  users: User[];
}

type SetUsers = ActionRequest<ActionType.SET_USERS, Meta>;

type Actions = SetUsers;

const Actions = {
  setUsers: (users: User[]): SetUsers => ({ type: ActionType.SET_USERS, meta: { users } }),
};

export const ActionCreators = {
  setUsers: Actions.setUsers,
};

type ReducerState = {
  users: User[];
  previousUsers: User[];
};

const initialState: ReducerState = {
  users: [],
  previousUsers: [],
};

export default function currentFolder(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.SET_USERS: {
      return {
        users: action.meta.users,
        previousUsers: state.users,
      };
    }
    default:
      return state;
  }
}

export const Selectors = {
  getPreviousUsers: (state: StoreState) => state.analyticsWatchers.actionsWatcher.previousUsers,
};

function* createUserSuccess(action: Ducks.Household.CreateUserSuccess): SagaIterator {
  yield call(addSettingsActionEvent, ActionsEventActionNames.CREATE_PROFILE, action.payload.firstName);
}

function* createUserFail(action: Ducks.Household.CreateUserFail): SagaIterator {
  yield call(addSettingsActionEvent, ActionsEventActionNames.CREATE_PROFILE, action.meta, undefined, action.error);
}

function* updateUserSuccess(action: Ducks.Household.UpdateUserSuccess): SagaIterator {
  const previousUsers: User[] = yield select(Selectors.getPreviousUsers);
  const oldValue = previousUsers.find((u) => action.payload.id === u.id);
  yield call(
    addSettingsActionEvent,
    ActionsEventActionNames.EDIT_PROFILE,
    action.payload.firstName,
    oldValue?.firstName
  );
}

function* updateUserFail(action: Ducks.Household.UpdateUserFail): SagaIterator {
  const previousUsers: User[] = yield select(Selectors.getPreviousUsers);
  const oldValue = previousUsers.find((u) => action.meta.id === u.id);
  yield call(
    addSettingsActionEvent,
    ActionsEventActionNames.EDIT_PROFILE,
    action.meta.firstName,
    oldValue?.firstName,
    action.error
  );
}

function* removeUserSuccess(action: Ducks.Household.RemoveUserSuccess): SagaIterator {
  const previousUsers: User[] = yield select(Selectors.getPreviousUsers);
  const oldValue = previousUsers.find((u) => action.meta === u.id);
  yield call(addSettingsActionEvent, ActionsEventActionNames.DELETE_PROFILE, undefined, oldValue?.firstName);
}

function* removeUserFail(action: Ducks.Household.RemoveUserFail): SagaIterator {
  const previousUsers: User[] = yield select(Selectors.getPreviousUsers);
  const oldValue = previousUsers.find((u) => action.meta === u.id);
  yield call(
    addSettingsActionEvent,
    ActionsEventActionNames.DELETE_PROFILE,
    undefined,
    oldValue?.firstName,
    action.error
  );
}

export function* saga(): SagaIterator {
  yield all([takeEvery(Ducks.Household.ActionType.createUser.SUCCESS, createUserSuccess)]);
  yield all([takeEvery(Ducks.Household.ActionType.createUser.FAIL, createUserFail)]);
  yield all([takeEvery(Ducks.Household.ActionType.updateUser.SUCCESS, updateUserSuccess)]);
  yield all([takeEvery(Ducks.Household.ActionType.updateUser.FAIL, updateUserFail)]);
  yield all([takeEvery(Ducks.Household.ActionType.removeUser.SUCCESS, removeUserSuccess)]);
  yield all([takeEvery(Ducks.Household.ActionType.removeUser.FAIL, removeUserFail)]);
}
